import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import CourseOfStudyTracker from '../components/CourseOfStudyTracker';
import '../styles/CourseOfStudy.css';

const CourseOfStudy = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm]);

  const fetchCandidates = async () => {
    try {
      const candidatesRef = collection(db, 'candidates');
      const q = query(candidatesRef, orderBy('lastName', 'asc'));
      const snapshot = await getDocs(q);
      
      const candidatesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCandidates(candidatesList);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCandidates = () => {
    let filtered = candidates;

    if (searchTerm) {
      filtered = filtered.filter(candidate =>
        `${candidate.firstName} ${candidate.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCandidates(filtered);
  };

  const getConferenceStatusLabel = (status) => {
    const statuses = {
      'lay': 'Lay',
      'clm': 'Certified Lay Ministry',
      'cmc': 'Certified Ministry Candidate',
      'supply-pastor': 'Supply Pastor',
      'local-pastor': 'Local Pastor',
      'other-fellowship': 'Other Fellowship',
      'other': 'Other'
    };
    return statuses[status] || status;
  };

  const calculateProgress = (candidate) => {
    const courses = candidate.courseOfStudy?.courses || [];
    const completedCourses = courses.filter(course => course.status === 'completed');
    const totalCredits = courses.reduce((sum, course) => sum + (course.credits || 0), 0);
    const completedCredits = completedCourses.reduce((sum, course) => sum + (course.credits || 0), 0);
    
    return {
      totalCourses: courses.length,
      completedCourses: completedCourses.length,
      totalCredits,
      completedCredits,
      progressPercentage: totalCredits > 0 ? (completedCredits / totalCredits) * 100 : 0
    };
  };

  if (loading) {
    return (
      <div className="course-of-study-container">
        <div className="loading-spinner">Loading candidates...</div>
      </div>
    );
  }

  return (
    <div className="course-of-study-container">
      <div className="page-header">
        <h1>Course of Study Progress</h1>
        <p>Track educational progress for all ministry candidates</p>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Search candidates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="candidates-progress-grid">
        {filteredCandidates.length === 0 ? (
          <div className="no-candidates">
            <p>No candidates found.</p>
          </div>
        ) : (
          filteredCandidates.map(candidate => {
            const progress = calculateProgress(candidate);
            const recentCourses = (candidate.courseOfStudy?.courses || [])
              .slice(-3)
              .reverse();

            return (
              <div key={candidate.id} className="candidate-progress-card">
                <div className="candidate-header">
                  <h3>{candidate.firstName} {candidate.lastName}</h3>
                  <span className={`candidate-type ${candidate.conferenceStatus || candidate.candidateType}`}>
                    {getConferenceStatusLabel(candidate.conferenceStatus || candidate.candidateType)}
                  </span>
                </div>

                <div className="progress-info">
                  <div className="progress-stats">
                    <div className="stat">
                      <span className="stat-value">{progress.completedCourses}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{progress.totalCourses}</span>
                      <span className="stat-label">Total Courses</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{progress.completedCredits}</span>
                      <span className="stat-label">Credits</span>
                    </div>
                  </div>

                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${progress.progressPercentage}%` }}
                    ></div>
                  </div>
                </div>

                <div className="course-summary">
                  <div className="recent-courses">
                    <h4>Recent Courses</h4>
                    {recentCourses.length > 0 ? (
                      recentCourses.map((course, index) => (
                        <div key={index} className="course-item">
                          <span className="course-code">{course.courseCode}</span>
                          <span className={`course-status ${course.status}`}>
                            {course.status}
                          </span>
                          {course.grade && (
                            <span className="course-grade">{course.grade}</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="no-courses">No courses recorded</div>
                    )}
                  </div>
                </div>

                <div className="card-actions">
                  <a 
                    href={`/candidates/${candidate.id}?tab=course-of-study`}
                    className="view-details-btn"
                  >
                    View Course Details
                  </a>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Candidates</h3>
          <p className="stat-number">{candidates.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active in Courses</h3>
          <p className="stat-number">
            {candidates.filter(c => (c.courseOfStudy?.courses || []).length > 0).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Completed Programs</h3>
          <p className="stat-number">
            {candidates.filter(c => {
              const courses = c.courseOfStudy?.courses || [];
              return courses.length > 0 && courses.every(course => course.status === 'completed');
            }).length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CourseOfStudy;
